import { useNavigate, useLocation } from 'react-router-dom';
import { PATH_APP } from '../../../../routes/paths';
import { sharedStyles } from 'styles/sharedStyles';
import {
  useTheme,
  Box,
  Badge,
  BOTTOM_NAV_HEIGHT,
  NavigationButton,
  getSafeAreaBottom,
  getBottomWithSafeArea,
} from '@planitgeo/treeplotter-ui-library';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import { useOnlineStatus } from 'common/infrastructure/network/onlineStatus/OnlineStatusContext';
import { useDispatch } from 'redux/store';
import { clearAppliedWorkListFiltersForMapNav } from '../../redux/uiState';
import { useMapLoadingOptional } from '../../loading/MapLoadingContext';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { AnimatedMapIcon } from '../../../../components/animatedMapIcon';
import { LocationOff } from '@mui/icons-material';

const bottomNavStyles = {
  'bottom-nav-container': {
    boxShadow: 'none',
    position: 'fixed',
    height: getBottomWithSafeArea(BOTTOM_NAV_HEIGHT),
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    bgcolor: 'primary.main',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '8px 0',
    paddingBottom: `calc(8px + ${getSafeAreaBottom()})`,
  },
  'bottom-nav-badge-container': {
    '& .MuiBadge-badge': {
      padding: '0 2px',
      minWidth: '16px',
      height: '16px',
      boxShadow: '0 2px 2px rgba(0,0,0,0.5)',
    },
  },
};

type Props = {
  title?: string;
};

export default function BottomNav({ title }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isOnline } = useOnlineStatus();
  const { tenant } = TenantRepository.useCurrentTenant();
  const mapLoading = useMapLoadingOptional();

  const isTenantLoaded = Boolean(tenant?.id);

  // Check if we're on a map page and if map is loading
  const isOnMapPage = location.pathname.startsWith(PATH_APP.inventory);
  const isMapLoading = mapLoading && (!mapLoading.isMapReady || mapLoading.isAssetsLoading);
  const hasAssetsError = mapLoading?.hasAssetsError || false;

  const isHomeSelected = location.pathname === PATH_APP.root;
  const isMapSelected = location.pathname.startsWith(PATH_APP.inventory);
  const isSettingsSelected = location.pathname.startsWith(PATH_APP.settings);

  // Component for the map icon with loading indicator
  const MapIcon = () => <AnimatedMapIcon isLoading={Boolean(isOnMapPage && isMapLoading)} />;

  return (
    <Box sx={bottomNavStyles['bottom-nav-container']}>
      <NavigationButton
        icon={<HomeIcon />}
        selected={isHomeSelected}
        iconColor="common.white"
        textColor="common.white"
        backgroundColor="transparent"
        selectionColor={theme.palette.secondary.main}
        onClick={() => navigate(PATH_APP.root)}
        disabled={!isTenantLoaded}
        aria-label="Navigate to home"
        data-testid="home_nav_button"
      />
      <NavigationButton
        icon={
          <Badge
            badgeContent={
              hasAssetsError && isOnMapPage && !isMapLoading ? (
                <LocationOff sx={sharedStyles['icon-extra-small']} />
              ) : null
            }
            color="error"
            sx={bottomNavStyles['bottom-nav-badge-container']}
          >
            <MapIcon />
          </Badge>
        }
        selected={isMapSelected}
        iconColor="common.white"
        textColor="common.white"
        backgroundColor="transparent"
        selectionColor={theme.palette.secondary.main}
        onClick={() => {
          dispatch(clearAppliedWorkListFiltersForMapNav());
          navigate(`${PATH_APP.inventory}/map?triggerUserLocation=true`);
        }}
        disabled={!isTenantLoaded}
        aria-label="Navigate to map"
        data-testid="map_nav_button"
      />
      <NavigationButton
        icon={
          <Badge
            badgeContent={
              isOnline ? null : <SyncDisabledIcon sx={sharedStyles['icon-extra-small']} />
            }
            color="warning"
            sx={bottomNavStyles['bottom-nav-badge-container']}
          >
            <SettingsIcon />
          </Badge>
        }
        selected={isSettingsSelected}
        iconColor="common.white"
        textColor="common.white"
        backgroundColor="transparent"
        selectionColor={theme.palette.secondary.main}
        onClick={() => navigate(PATH_APP.settings)}
        disabled={!isTenantLoaded}
        aria-label="Navigate to settings"
        data-testid="settings_nav_button"
      />
    </Box>
  );
}
